import type { UserContext } from '../../context/user-context.js'
import { LocalPersistenceModeCapabilities, type LocalPersistencePolicy } from './storage-policy.js'
import type { FamilyPersistence } from './family-persistence.js'
import type { FamilyAuthenticationContextStore } from './stores/authentication-context-store.js'
import type { FamilyAttachmentMetadataStore } from './stores/attachment-metadata-store.js'
import {
    FamilyAttachmentStoreAdapter,
    type AttachmentCacheStorage,
    type FamilyAttachmentStore
} from './stores/attachment-store.js'
import type { FamilyClassroomStore } from './stores/classroom-store.js'
import type { FamilyChildStore } from './stores/child-store.js'
import type { FamilyEventStore } from './stores/event-store.js'
import type { FamilyFeatureStore } from './stores/feature-store.js'
import type { FamilyGuardianStore } from './stores/guardian-store.js'
import type { FamilyLocalEncryptionStore } from './stores/local-encryption-enrollment-store.js'
import type { MessagingStore } from './stores/messaging-store.js'
import type { FamilyUserContextStore } from './stores/user-context-store.js'
import type { ReadyRosieVideo, StoryWithPages } from '@tadpoles/common'

import type {
    LibraryCatalogBacking,
    LibraryCatalogMetadata,
    LibraryCategory
} from '../library/catalog.js'

const FamilyLocalBackingBrand: unique symbol = Symbol('FamilyLocalBacking')
const FamilyUserLocalBackingBrand: unique symbol = Symbol('FamilyUserLocalBacking')

export interface FamilyUserLocalBacking {
    readonly [FamilyUserLocalBackingBrand]: true
    getAttachmentStore(): Promise<FamilyAttachmentStore | undefined>
    getClassroomStore(): Promise<FamilyClassroomStore | undefined>
    getChildStore(): Promise<FamilyChildStore | undefined>
    getEventStore(): Promise<FamilyEventStore | undefined>
    getFeatureStore(): Promise<FamilyFeatureStore | undefined>
    getGuardianStore(): Promise<FamilyGuardianStore | undefined>
    getLocalEncryptionStore(): Promise<FamilyLocalEncryptionStore | undefined>
    getMessagingStore(): Promise<MessagingStore | undefined>
}

export interface FamilyLocalBacking {
    readonly [FamilyLocalBackingBrand]: true
    forUser(context: UserContext): FamilyUserLocalBacking
    getAuthenticationContextStore(): Promise<FamilyAuthenticationContextStore | undefined>
    getLibraryCatalogBacking(): LibraryCatalogBacking | undefined
    getUserContextStore(): Promise<FamilyUserContextStore | undefined>
}

export const NoFamilyUserLocalBacking: FamilyUserLocalBacking = {
    [FamilyUserLocalBackingBrand]: true,
    async getAttachmentStore() {
        return undefined
    },
    async getClassroomStore() {
        return undefined
    },
    async getChildStore() {
        return undefined
    },
    async getEventStore() {
        return undefined
    },
    async getFeatureStore() {
        return undefined
    },
    async getGuardianStore() {
        return undefined
    },
    async getLocalEncryptionStore() {
        return undefined
    },
    async getMessagingStore() {
        return undefined
    }
}

export const NoFamilyLocalBacking: FamilyLocalBacking = {
    [FamilyLocalBackingBrand]: true,
    forUser() {
        return NoFamilyUserLocalBacking
    },
    async getAuthenticationContextStore() {
        return undefined
    },
    getLibraryCatalogBacking() {
        return undefined
    },
    async getUserContextStore() {
        return undefined
    }
}

export type FamilyLocalBackingOptions = {
    attachmentCache?: {
        cacheStorage: AttachmentCacheStorage
        requestPersistentStorage?: () => Promise<boolean>
    }
    localPersistencePolicy: LocalPersistencePolicy
    persistence: FamilyPersistence
}

class PolicyBoundFamilyUserLocalBacking implements FamilyUserLocalBacking {
    public readonly [FamilyUserLocalBackingBrand] = true
    readonly #context: UserContext
    readonly #attachmentCache?: FamilyLocalBackingOptions['attachmentCache']
    readonly #localPersistencePolicy: LocalPersistencePolicy
    readonly #persistence: FamilyPersistence
    #persistentStorageRequested = false

    public constructor(
        context: UserContext,
        localPersistencePolicy: LocalPersistencePolicy,
        persistence: FamilyPersistence,
        attachmentCache?: FamilyLocalBackingOptions['attachmentCache']
    ) {
        this.#context = context
        this.#attachmentCache = attachmentCache
        this.#localPersistencePolicy = localPersistencePolicy
        this.#persistence = persistence
    }

    public async getAttachmentStore(): Promise<FamilyAttachmentStore | undefined> {
        if (!this.#canUseUserScopedData() || !this.#attachmentCache) {
            return undefined
        }

        if (!this.#persistentStorageRequested && this.#attachmentCache.requestPersistentStorage) {
            this.#persistentStorageRequested = true
            try {
                // Browser approval improves durability but never grants permission to store user data.
                await this.#attachmentCache.requestPersistentStorage()
            } catch {
                // Browser persistence is best effort; attachment downloads remain available.
            }
        }

        let metadataStore: FamilyAttachmentMetadataStore | undefined
        try {
            metadataStore = await this.#persistence.getAttachmentMetadataStore(this.#context)
        } catch {
            // CacheStorage remains usable offline when IndexedDB metadata is unavailable.
        }

        return new FamilyAttachmentStoreAdapter({
            cacheStorage: this.#attachmentCache.cacheStorage,
            metadataStore,
            personKey: this.#context.person.key
        })
    }

    public async getClassroomStore(): Promise<FamilyClassroomStore | undefined> {
        if (!this.#canUseUserScopedData()) {
            return undefined
        }

        return await this.#persistence.getClassroomStore(this.#context)
    }

    public async getChildStore(): Promise<FamilyChildStore | undefined> {
        if (!this.#canUseUserScopedData()) {
            return undefined
        }

        return await this.#persistence.getChildStore(this.#context)
    }

    public async getEventStore(): Promise<FamilyEventStore | undefined> {
        return undefined
    }

    public async getFeatureStore(): Promise<FamilyFeatureStore | undefined> {
        if (!this.#canUseUserScopedData()) {
            return undefined
        }

        return await this.#persistence.getFeatureStore(this.#context)
    }

    public async getGuardianStore(): Promise<FamilyGuardianStore | undefined> {
        if (!this.#canUseUserScopedData()) {
            return undefined
        }

        return await this.#persistence.getGuardianStore(this.#context)
    }

    public async getLocalEncryptionStore(): Promise<FamilyLocalEncryptionStore | undefined> {
        if (
            !LocalPersistenceModeCapabilities[
                this.#localPersistencePolicy.localEncryptionEnrollment
            ].cleartext
        ) {
            return undefined
        }

        return await this.#persistence.getLocalEncryptionStore(this.#context)
    }

    public async getMessagingStore(): Promise<MessagingStore | undefined> {
        if (!this.#canUseUserScopedData()) {
            return undefined
        }

        return await this.#persistence.getMessagingStore(this.#context)
    }

    #canUseUserScopedData(): boolean {
        return LocalPersistenceModeCapabilities[this.#localPersistencePolicy.userScopedData]
            .cleartext
    }
}

export class PolicyBoundFamilyLocalBacking implements FamilyLocalBacking {
    public readonly [FamilyLocalBackingBrand] = true
    readonly #localPersistencePolicy: LocalPersistencePolicy
    readonly #attachmentCache?: FamilyLocalBackingOptions['attachmentCache']
    readonly #persistence: FamilyPersistence
    #libraryCatalogBacking: LibraryCatalogBacking | undefined

    public constructor(options: FamilyLocalBackingOptions) {
        this.#attachmentCache = options.attachmentCache
        this.#localPersistencePolicy = options.localPersistencePolicy
        this.#persistence = options.persistence
    }

    public forUser(context: UserContext): FamilyUserLocalBacking {
        return new PolicyBoundFamilyUserLocalBacking(
            context,
            this.#localPersistencePolicy,
            this.#persistence,
            this.#attachmentCache
        )
    }

    public async getAuthenticationContextStore(): Promise<
        FamilyAuthenticationContextStore | undefined
    > {
        if (
            !LocalPersistenceModeCapabilities[this.#localPersistencePolicy.authenticationContext]
                .cleartext
        ) {
            return undefined
        }

        return await this.#persistence.getAuthenticationContextStore()
    }

    public getLibraryCatalogBacking(): LibraryCatalogBacking | undefined {
        if (
            !LocalPersistenceModeCapabilities[this.#localPersistencePolicy.libraryCatalogs]
                .cleartext
        ) {
            return undefined
        }

        this.#libraryCatalogBacking ??= new LazyFamilyLibraryCatalogBacking(this.#persistence)
        return this.#libraryCatalogBacking
    }

    public async getUserContextStore(): Promise<FamilyUserContextStore | undefined> {
        if (!this.#canUseUserScopedData()) {
            return undefined
        }

        return await this.#persistence.getUserContextStore()
    }

    #canUseUserScopedData(): boolean {
        return LocalPersistenceModeCapabilities[this.#localPersistencePolicy.userScopedData]
            .cleartext
    }
}

class LazyFamilyLibraryCatalogBacking implements LibraryCatalogBacking {
    #metadataStore: ReturnType<FamilyPersistence['getLibraryCatalogMetadataStore']> | undefined
    #storyStore: ReturnType<FamilyPersistence['getLibraryStoryStore']> | undefined
    #videoStore: ReturnType<FamilyPersistence['getLibraryVideoStore']> | undefined
    readonly #persistence: FamilyPersistence

    public constructor(persistence: FamilyPersistence) {
        this.#persistence = persistence
    }

    public async loadStories(signal: AbortSignal): Promise<unknown | null> {
        return await (await this.#getStoryStore()).load(signal)
    }

    public async loadVideos(signal: AbortSignal): Promise<unknown | null> {
        return await (await this.#getVideoStore()).load(signal)
    }

    public async loadMetadata(
        category: LibraryCategory,
        signal: AbortSignal
    ): Promise<unknown | null> {
        return await (await this.#getMetadataStore()).load(category, signal)
    }

    public async persistStories(
        stories: StoryWithPages[],
        metadata: LibraryCatalogMetadata,
        signal: AbortSignal
    ): Promise<void> {
        await (await this.#getStoryStore()).replaceAll(stories, signal)
        await (await this.#getMetadataStore()).save('ebook', metadata, signal)
    }

    public async persistVideos(
        videos: ReadyRosieVideo[],
        metadata: LibraryCatalogMetadata,
        signal: AbortSignal
    ): Promise<void> {
        await (await this.#getVideoStore()).replaceAll(videos, signal)
        await (await this.#getMetadataStore()).save('video', metadata, signal)
    }

    #getMetadataStore(): ReturnType<FamilyPersistence['getLibraryCatalogMetadataStore']> {
        return (this.#metadataStore ??= this.#persistence.getLibraryCatalogMetadataStore())
    }

    #getStoryStore(): ReturnType<FamilyPersistence['getLibraryStoryStore']> {
        return (this.#storyStore ??= this.#persistence.getLibraryStoryStore())
    }

    #getVideoStore(): ReturnType<FamilyPersistence['getLibraryVideoStore']> {
        return (this.#videoStore ??= this.#persistence.getLibraryVideoStore())
    }
}

export function createFamilyLocalBacking(options: FamilyLocalBackingOptions): FamilyLocalBacking {
    return new PolicyBoundFamilyLocalBacking(options)
}
